import { post, put, remove } from '../Services/http';
import { load as loadCart } from './Cart.action';
import { catchError } from './Error.action';

import {
    CHECKOUT_ACCEPT_TERMS_OF_CONDITION,
    CHECKOUT_SET_ADDITIONALDATA,
    CHECKOUT_SET_BILLING_AS_DELIVERY,
    CHECKOUT_SET_SELECTED_COMPANY_ADDRESS,
    CHECKOUT_SET_PRIVATE_CUSTOMER,
    CHECKOUT_SET_SIGN_UP,
    CHECKOUT_SET_DELIVERY,
    CHECKOUT_SET_SHIPPING,
    CHECKOUT_SET_COUNTRY,
    CHECKOUT_SET_DISABLED_STATUS,
    CHECKOUT_SET_DISCOUNT_CODE,
    CHECKOUT_SET_ORDER_NOTE,
    CHECKOUT_SET_PAYMENT,
    CHECKOUT_SET_PAYMENT_WIDGET,
    CHECKOUT_SET_SHIPMENT_WIDGET,
    CHECKOUT_SET_SHOW_ALTERNATIVE_ADDRESS,
    CHECKOUT_SET_STATUS_SUBMIT_BUTTON,
    CHECKOUT_SET_USED_DISCOUNT_CODE,
    CHECKOUT_SUBMIT,
    CHECKOUT_SUBMIT_ERROR,
    CHECKOUT_UPDATE_CUSTOMER_INFO,
    CHECKOUT_VALIDATE_ADDRESS,
    CHECK_IF_USER_EXISTS,
    CLEAR_ERROR,
    CHECKOUT_SET_PAYMENTUPDATING,
    CHECKOUT_SET_SHIPPING_OPTION,
} from '../constants';
import { hideLoadSpinner, showLoadSpinner } from '../Services/Spinner';

export const setCheckoutDisabledStatus = (status) => ({
    type: CHECKOUT_SET_DISABLED_STATUS,
    payload: {
        isCheckoutDisabled: status,
    },
});

export const setBusinessCustomer = (isBusinessCustomer) => ({
    type: CHECKOUT_SET_PRIVATE_CUSTOMER,
    payload: {
        isBusinessCustomer,
    },
});

export const setSignUp = (signUp) => ({
    type: CHECKOUT_SET_SIGN_UP,
    payload: {
        signUp,
    },
});

export const updateCustomerDetails = (key, data) => ({
    type: CHECKOUT_UPDATE_CUSTOMER_INFO,
    payload: {
        key,
        data,
    },
});

export const setBillingAddressAsDelivery = (billingAddressAsDelivery) => ({
    type: CHECKOUT_SET_BILLING_AS_DELIVERY,
    payload: {
        billingAddressAsDelivery,
    },
});

export const setSelectedCompanyAddress = (selectedCompanyAddressId) => ({
    type: CHECKOUT_SET_SELECTED_COMPANY_ADDRESS,
    payload: {
        selectedCompanyAddressId,
    },
});

export const setShipping = (shippingMethod) => (dispatch, getState) => {
    dispatch({
        type: CHECKOUT_SET_SHIPPING,
        payload: {
            selectedShippingMethod: shippingMethod,
        },
    });

    dispatch(setPaymentUpdating(true));
    return put('/api/checkout/deliveryProvider', shippingMethod.id)
        .then((response) => response.json())
        .then((result) => {
            dispatch(loadCart());
            if (result) {
                dispatch({
                    type: CHECKOUT_SET_SHIPPING,
                    payload: {
                        selectedShippingMethod: result,
                    },
                });
            }

            const { selectedPaymentMethod } = getState().checkout.payload;
            if (selectedPaymentMethod?.id) {
                return dispatch(reloadPayment());
            }
        })
        .catch((ex) => dispatch(catchError(ex, (error) => submitError(error))))
        .finally(() => dispatch(setPaymentUpdating(false)));
};

export const setDelivery = (method) => (dispatch, getState) => {
    if (!method?.id || !method?.integrationType) {
        return Promise.resolve();
    }

    dispatch({
        type: CHECKOUT_SET_DELIVERY,
        payload: {
            selectedDeliveryMethod: method,
        },
    });
    const { payload } = getState().checkout;
    const request = {
        selectedDeliveryMethodId: payload.selectedDeliveryMethod?.id,
        selectedPaymentMethodId: payload.selectedPaymentMethod?.id,
        updatePaymentWidget: !!payload.selectedPaymentMethod?.id,
    };
    dispatch(setPaymentUpdating(true));
    return put('/api/checkout/setDeliveryProvider', request)
        .then((response) => response.json())
        .then((result) => {
            dispatch(loadCart());
            if (result !== null && result.paymentWidget) {
                dispatch(setPaymentWidget(result.paymentWidget));
            }
            dispatch(setShipmentWidget(result.shipmentWidget));
            dispatch(setSelectedShippingOption(null));
        })
        .catch((ex) => dispatch(catchError(ex, (error) => submitError(error))))
        .finally(() => dispatch(setPaymentUpdating(false)));
};

export const setAdditionalData = (data) => (dispatch) => {
    return put('/api/checkout/additionalData', data)
        .then((response) => response.json())
        .then((result) => {
            dispatch({
                type: CHECKOUT_SET_ADDITIONALDATA,
                payload: result,
            });
        })
        .catch((ex) => dispatch(catchError(ex, (error) => submitError(error))));
};

export const setShippingServiceFee = (fee) => (dispatch) => {
    if (!fee) {
        fee = { type: '0' }; // The PhoneServiceType.None has value 0.
    }
    dispatch(setPaymentUpdating(true));
    return put('/api/checkout/additionalShippingService', fee)
        .then((response) => response.json())
        .then(() => {
            dispatch(loadCart());
            dispatch(reloadPayment());
        })
        .catch((ex) => {
            dispatch(catchError(ex, (error) => submitError(error)));
        })
        .finally(() => dispatch(setPaymentUpdating(false)));
};

export const setPaymentUpdating = (updating) => {
    return {
        type: CHECKOUT_SET_PAYMENTUPDATING,
        payload: updating,
    };
};

export const setCountry = (systemId) => (dispatch, getState) => {
    dispatch({
        type: CHECKOUT_SET_COUNTRY,
        payload: {
            selectedCountry: systemId,
        },
    });
    const { payload } = getState().checkout;

    // reset paymentWidget to null to avoid sending unnecessary request to payment provider
    dispatch(setPaymentWidget(null));
    dispatch(setShipmentWidget(null));

    return put('/api/checkout/setCountry', payload)
        .then((response) => response.json())
        .then((result) => {
            dispatch(loadCart());
            dispatch(setPaymentWidget(result.paymentWidget));
            dispatch(setShipmentWidget(result.shipmentWidget));
            dispatch({
                type: CHECKOUT_SET_DELIVERY,
                payload: {
                    deliveryMethods: result.deliveryMethods,
                    selectedDeliveryMethod: result.selectedDeliveryMethod,
                },
            });
            dispatch({
                type: CHECKOUT_SET_PAYMENT,
                payload: {
                    paymentMethods: result.paymentMethods,
                    selectedPaymentMethod: result.selectedPaymentMethod,
                },
            });
        })
        .catch((ex) => dispatch(catchError(ex, (error) => submitError(error))));
};

export const setPayment = (method) => (dispatch, getState) => {
    dispatch({
        type: CHECKOUT_SET_PAYMENT,
        payload: {
            selectedPaymentMethod: method,
        },
    });

    const checkoutState = getState().checkout.payload;
    const request = {
        paymentWidget: checkoutState.paymentWidget,
        selectedPaymentMethod: checkoutState.selectedPaymentMethod,
    };

    // reset paymentWidget to null to avoid sending unnecessary request to payment provider
    dispatch(setPaymentWidget(null));
    dispatch(setPaymentUpdating(true));

    // reset payment error on the checkout
    dispatch(
        submitError({
            payment: [],
        })
    );

    return put('/api/checkout/paymentProvider', request)
        .then((response) => response.json())
        .then((result) => {
            dispatch(loadCart());
            dispatch(setPaymentWidget(result.paymentWidget));
        })
        .catch((ex) => dispatch(catchError(ex, (error) => submitError(error))))
        .finally(() => dispatch(setPaymentUpdating(false)));
};

export const reloadPayment = () => (dispatch, getState) => {
    const checkoutState = getState().checkout.payload;
    const request = {
        paymentWidget: checkoutState.paymentWidget,
        selectedPaymentMethod: checkoutState.selectedPaymentMethod,
    };

    dispatch(setPaymentUpdating(true));

    return put('/api/checkout/reloadPaymentWidget', request)
        .then((response) => response.json())
        .then((result) => {
            if (result && result.paymentWidget) {
                dispatch(setPaymentWidget(result.paymentWidget));
            }
        })
        .catch((ex) => dispatch(catchError(ex, (error) => submitError(error))))
        .finally(() => dispatch(setPaymentUpdating(false)));
};

export const reloadShipment = () => (dispatch, getState) => {
    const { payload } = getState().checkout;
    return put('/api/checkout/reloadShipmentWidget', payload)
        .then((response) => response.json())
        .then((result) => {
            if (result && result.shipmentWidget) {
                dispatch(setShipmentWidget(result.shipmentWidget));
            }
        })
        .catch((ex) => dispatch(catchError(ex, (error) => submitError(error))));
};

export const setShippingOption = ({ shippingInfo, onSuccess, onError }) => (
    dispatch,
    getState
) => {
    const { payload } = getState().checkout;
    return put('/api/checkout/setShippingOption', {
        ...payload,
        shippingInfo,
    })
        .then((response) => response.json())
        .then((result) => {
            if (result?.paymentWidget) {
                dispatch(setPaymentWidget(result.paymentWidget));
            }
            dispatch(setSelectedShippingOption(shippingInfo));
            dispatch(loadCart());
            onSuccess && onSuccess();
        })
        .catch((ex) =>
            dispatch(
                catchError(ex, (error) => {
                    submitError(error);
                    onError && onError();
                })
            )
        );
};

const setSelectedShippingOption = (shippingInfo) => ({
    type: CHECKOUT_SET_SHIPPING_OPTION,
    payload: {
        shippingInfo,
    },
});

const setPaymentWidget = (paymentWidget) => ({
    type: CHECKOUT_SET_PAYMENT_WIDGET,
    payload: {
        paymentWidget,
    },
});

const setShipmentWidget = (shipmentWidget) => ({
    type: CHECKOUT_SET_SHIPMENT_WIDGET,
    payload: {
        shipmentWidget,
    },
});

export const setOrderNote = (orderNote) => ({
    type: CHECKOUT_SET_ORDER_NOTE,
    payload: {
        orderNote,
    },
});

export const setAcceptTermsOfCondition = (acceptTermsOfCondition) => ({
    type: CHECKOUT_ACCEPT_TERMS_OF_CONDITION,
    payload: {
        acceptTermsOfCondition,
    },
});

export const setDiscountCode = (discountCode) => ({
    type: CHECKOUT_SET_DISCOUNT_CODE,
    payload: {
        discountCode,
    },
});

export const submitDiscountCode = () => (dispatch, getState) => {
    const { payload } = getState().checkout;
    dispatch(setPaymentUpdating(true));
    return put('/api/checkout/setDiscountCode', payload)
        .then((response) => response.json())
        .then((result) => {
            dispatch(setUsedDiscountCodes(result.usedDiscountCodes));
            dispatch(loadCart());
            if (result?.shipmentWidget) {
                dispatch(setShipmentWidget(result.shipmentWidget));
            }
            dispatch(setPaymentWidget(result.paymentWidget));
            // reset error of campaign code
            dispatch(
                submitError({
                    modelState: {
                        discountCode: [],
                    },
                })
            );
        })
        .catch((ex) => {
            dispatch(catchError(ex, (error) => submitError(error)));
            // restore the initial cart
            dispatch(loadCart());
        })
        .finally(() => dispatch(setPaymentUpdating(false)));
};

export const deleteDiscountCode = (discountCode) => (dispatch, getState) => {
    const { payload } = getState().checkout;
    payload.discountCode = discountCode;
    dispatch(setPaymentUpdating(true));
    return remove('/api/checkout/deleteDiscountCode', payload)
        .then((response) => response.json())
        .then((result) => {
            dispatch(setUsedDiscountCodes(result.usedDiscountCodes));
            dispatch(loadCart());
            if (result?.shipmentWidget) {
                dispatch(setShipmentWidget(result.shipmentWidget));
            }
            dispatch(setPaymentWidget(result.paymentWidget));
            dispatch(setDiscountCode(''));
            // reset error of campaign code
            dispatch(
                submitError({
                    modelState: {
                        discountCode: [],
                    },
                })
            );
        })
        .catch((ex) => {
            dispatch(catchError(ex, (error) => submitError(error)));
            // restore the initial cart
            dispatch(loadCart());
        })
        .finally(() => dispatch(setPaymentUpdating(false)));
};

const setUsedDiscountCodes = (usedDiscountCodes) => ({
    type: CHECKOUT_SET_USED_DISCOUNT_CODE,
    payload: {
        usedDiscountCodes,
    },
});

export const submit = () => (dispatch, getState) => {
    const { payload } = getState().checkout;
    return _submit('/api/checkout', payload, dispatch);
};

const _submit = (url, model, dispatch) => {
    showLoadSpinner();
    return post(url, model)
        .then((response) => response.json())
        .then((result) => {
            dispatch(submitDone(result));
        })
        .catch((ex) => {
            if (ex.response) {
                ex.response.json().then((error) => {
                    dispatch(
                        submitError({
                            ...error,
                        })
                    );
                    dispatch(submitDone(null));
                    // reload the cart, it might be changed after validation
                    dispatch(loadCart());
                });
            } else {
                dispatch(submitError(ex));
            }
            dispatch(setStatusSubmitButton(true));
        })
        .finally(() => hideLoadSpinner());
};

export const saveCustomerDetail = (data) => (dispatch, getState) => {
    const { payload } = getState().checkout;
    const updatedData = { ...payload, ...data };
    return put('/api/checkout/setCustomerDetail', updatedData)
        .then((response) => response.json())
        .then(() => dispatch(setStatusSubmitButton(true)))
        .catch((ex) => {
            if (ex.response) {
                ex.response.json().then((error) => {
                    dispatch(submitError(error));
                    dispatch(setStatusSubmitButton(false));
                });
            } else {
                dispatch(submitError(ex));
            }
            throw ex;
        });
};

export const clearError = () => ({
    type: CLEAR_ERROR,
});

export const submitDone = (result) => ({
    type: CHECKOUT_SUBMIT,
    payload: {
        result,
    },
});

export const submitError = (error) => ({
    type: CHECKOUT_SUBMIT_ERROR,
    payload: {
        error,
    },
});

export const setStatusSubmitButton = (value) => ({
    type: CHECKOUT_SET_STATUS_SUBMIT_BUTTON,
    payload: {
        enableConfirmButton: value,
    },
});

export const setShowAlternativeAddress = (showAlternativeAddress) => ({
    type: CHECKOUT_SET_SHOW_ALTERNATIVE_ADDRESS,
    payload: {
        showAlternativeAddress,
    },
});
export const setValidateStatus = (isValidating) => ({
    type: CHECKOUT_VALIDATE_ADDRESS,
    payload: {
        isValidating,
    },
});

export const checkIfUserExists = (organizationNo, username) => (dispatch) => {
    return put('/api/checkout/checkUserExists/', { organizationNo, username })
        .then((response) => response.json())
        .then((result) => {
            dispatch({
                type: CHECK_IF_USER_EXISTS,
                payload: {
                    checkUser: result,
                },
            });
        })
        .catch((ex) => dispatch(catchError(ex, (error) => submitError(error))));
};
